import * as React from 'react';

import { Product } from '../../../shared/types/models';
import { 
  CartView, 
  CTLineItem, 
  CTCategory, 
  CartMetadataItem,
  CartItemAttribute,
  CTProduct
} from '../types/cartTypes';
import { normalizeProducts } from '../utils/cartNormalization';
import { getProductsByIds, getCategoriesByIds } from '../utils/cartUtil';

interface AvailableAddOns {
  products: Product[];
  productAddOnsMap: Record<string, string[]>;
}

/**
 * @param {Object|null} cart - Current cart with lineItems
 * @param {{ locale: string }} config - App config (e.g. config.locale)
 */
export function useCartPageMetadata(cart: CartView | null, config: { locale?: string } = { locale: 'en' }) {
  const [productMetadata, setProductMetadata] = React.useState<Record<string, CartMetadataItem>>({});
  const [availableAddOns, setAvailableAddOns] = React.useState<AvailableAddOns>({
    products: [],
    productAddOnsMap: {},
  });
  const [metadataLoaded, setMetadataLoaded] = React.useState(false);
  const metadataRef = React.useRef(productMetadata);
  metadataRef.current = productMetadata;

  // Extract primitive so it can be listed directly in the useEffect dep array
  const locale = config.locale || 'en';

  React.useEffect(() => {
    const fetchMetadata = async () => {
      if (!cart || !cart.lineItems || cart.lineItems.length === 0) {
        setMetadataLoaded(true);
        return;
      }

      const productIds = new Set<string>();
      const addOnIds = new Set<string>();
      cart.lineItems.forEach((li: CTLineItem) => {
        productIds.add(li.productId);
        if (li.custom?.fields?.attachedAddOns) {
          li.custom.fields.attachedAddOns.forEach((id: string) => addOnIds.add(id));
        }
      });

      const allIds = new Set<string>([...productIds, ...addOnIds]);
      if (allIds.size === 0) {
        setMetadataLoaded(true);
        return;
      }

      const currentMetadata = metadataRef.current;
      const missingIds = Array.from(allIds).filter((id) => !currentMetadata[id]);
      if (missingIds.length === 0) {
        setMetadataLoaded(true);
        return;
      }

      setMetadataLoaded(false);

      const fetchedProducts = await getProductsByIds(missingIds);
      const products = normalizeProducts(fetchedProducts);

      const categoryIds = new Set<string>();
      const relatedAddOnIds = new Set<string>();
      products.forEach((p: CTProduct) => {
        if (productIds.has(p.id)) {
          const data = p.masterData?.current || p.masterData?.staged || p;
          const categories = data.categories || p.categories || [];
          categories.forEach((c: { id: string }) => categoryIds.add(c.id));

          const mv = data.masterVariant || p.masterVariant;
          const attributes = mv?.attributes || [];
          const relatedAddOnsAttr = attributes.find((a: CartItemAttribute) => a.name === 'relatedAddons');
          if (relatedAddOnsAttr?.value) {
            (relatedAddOnsAttr.value as Array<{ id: string }>).forEach((ref) => relatedAddOnIds.add(ref.id));
          }
        }
      });

      let categories: CTCategory[] = [];
      if (categoryIds.size > 0) {
        categories = await getCategoriesByIds(Array.from(categoryIds));
      }
      const categoryMap: Record<string, CTCategory> = {};
      categories.forEach((c) => {
        categoryMap[c.id] = c;
      });

      const configProductIds = new Set<string>();
      categories.forEach((c: CTCategory) => {
        if (c.custom?.fields?.configurationProduct?.id) {
          configProductIds.add(c.custom.fields.configurationProduct.id);
        }
      });

      let configProducts: Product[] = [];
      if (configProductIds.size > 0) {
        configProducts = await getProductsByIds(Array.from(configProductIds));
      }
      const configProductMap: Record<string, Product> = {};
      configProducts.forEach((cp) => {
        configProductMap[cp.id] = cp;
      });

      const missingAddOnIds = Array.from(relatedAddOnIds).filter(
        (id) => !products.find((p) => p.id === id)
      );
      if (missingAddOnIds.length > 0) {
        const fetchedAddOns = await getProductsByIds(missingAddOnIds);
        const addOnProducts = normalizeProducts(fetchedAddOns);
        products.push(...addOnProducts);
      }

      const newMetadata: Record<string, CartMetadataItem> = {};
      const productAddOnsMap: Record<string, string[]> = {};

      products.forEach((p: CTProduct) => {
        const data = p.masterData?.current || p.masterData?.staged || p;
        const nameObj = data.name || p.name || {};
        const name: string =
          nameObj[locale] ||
          nameObj['en'] ||
          (Object.values(nameObj)[0] as string) ||
          '';

        const mv = data.masterVariant || p.masterVariant;
        const priceObj = mv?.prices?.[0];
        let price = 0;
        let originalPrice: number | null = null;
        if (priceObj) {
          if (priceObj.discounted) {
            price = (priceObj.discounted.value?.centAmount || 0) / 100;
            originalPrice = (priceObj.value?.centAmount || 0) / 100;
          } else {
            price = (priceObj.value?.centAmount || 0) / 100;
          }
        }

        const attributes = mv?.attributes || [];
        const startAttr = attributes.find((a: CartItemAttribute) => a.name === 'startDate');
        const endAttr = attributes.find((a: CartItemAttribute) => a.name === 'endDate');
        const eventSessionsAttr = attributes.find((a: CartItemAttribute) => a.name === 'options');
        const rawOptions: unknown[] = (eventSessionsAttr?.value as unknown[]) || [];
        const isMultiOption = (attributes.find((a: CartItemAttribute) => a.name === 'allowMultiOptions')?.value as boolean) || false;
        const marketingTagAttr = attributes.find(
          (a: CartItemAttribute) => a.name === 'marketing-tag' || a.name === 'marketingTag'
        );
        const marketingTag = marketingTagAttr?.value;

        let venue = '';
        let location = '';
        if (productIds.has(p.id)) {
          const productCats = data.categories || p.categories || [];
          for (const catRef of productCats) {
            const cat = categoryMap[catRef.id];
            if (cat) {
              if (cat.custom?.fields?.venue || cat.custom?.fields?.location) {
                if (cat.custom?.fields?.venue) {
                  const v = cat.custom.fields.venue;
                  venue = typeof v === 'string' ? v : (v['en'] || v['en-US'] || (Object.values(v)[0] as string) || String(v));
                }
                if (cat.custom?.fields?.location) {
                  const l = cat.custom.fields.location;
                  location = typeof l === 'string' ? l : (l['en'] || l['en-US'] || (Object.values(l)[0] as string) || String(l));
                }
                break;
              }
            }
          }
        }

        newMetadata[p.id] = {
          id: p.id,
          name,
          price,
          originalPrice,
          startDate: startAttr?.value as string | undefined,
          endDate: endAttr?.value as string | undefined,
          venue,
          location,
          rawOptions,
          isMultiOption,
          marketingTag,
          discountName:
            priceObj?.discounted?.discount?.obj?.name?.[locale] ||
            priceObj?.discounted?.discount?.obj?.name?.['en'],
          prices: mv?.prices ?? [],
        };

        if (productIds.has(p.id)) {
          const relatedAddOnsAttr = attributes.find((a: CartItemAttribute) => a.name === 'relatedAddons');
          if (relatedAddOnsAttr?.value) {
            productAddOnsMap[p.id] = (relatedAddOnsAttr.value as Array<{ id: string }>).map((ref) => ref.id);
          } else {
            productAddOnsMap[p.id] = [];
          }
        }
      });

      setProductMetadata((prev) => ({ ...prev, ...newMetadata }));
      setAvailableAddOns({ products: products as unknown as Product[], productAddOnsMap });
      setMetadataLoaded(true);
    };

    fetchMetadata();
  }, [cart, locale]);

  return { productMetadata, availableAddOns, metadataLoaded };
}
